import { Button, ButtonVariant } from '@Core/Components'
import { Modal, ModalFooter, ModalSize } from '@Core/Components/Modal/Modal'
import { ModalProps } from '@Core/Components/Modal/Modal.types'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { usePermissions } from '@Core/Hooks/usePermissions'
import { getAttachmentUrl } from '@/utils/firebaseStorage'
import {
    AttachmentCategory,
    AttachmentEntityType,
    ATTACHMENT_CATEGORIES_BY_ENTITY,
    PaymentAttachmentGroup,
    ProcessAttachmentsAggregate,
    PurchasingAttachment,
    QuoteAttachmentGroup,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { roles } from '@Purchasing/roles'
import {
    attachmentCategoryLabel,
    attachmentEntityLabel,
    fileSizeLabel,
    formatDatePt,
} from '@Purchasing/presentation/purchasing-labels'
import { formatBRL } from '@Purchasing/Utils/money'
import { useEffect, useState } from 'react'

const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']

const imageMimes = ['image/jpeg', 'image/png', 'image/webp']

// ---------------------------------------------------------------------------
// AttachmentRow — linha utilizável: preview/ícone + Visualizar/Baixar/⋯ (§4)
// ---------------------------------------------------------------------------

interface AttachmentRowProps {
    attachment: PurchasingAttachment
    url?: string | null
    opening?: boolean
    onPreview?: (attachment: PurchasingAttachment) => void
    onDownload?: (attachment: PurchasingAttachment) => void
    onRemove?: (attachment: PurchasingAttachment) => void
}

export const AttachmentRow = ({
    attachment,
    url,
    opening,
    onPreview,
    onDownload,
    onRemove,
}: AttachmentRowProps) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [confirmRemove, setConfirmRemove] = useState(false)
    const isImage = imageMimes.includes(attachment.mimeType)

    return (
        <li
            className="flex items-center gap-3 py-2"
            data-testid={`attachment-row-${attachment._id}`}
        >
            {/* Thumb / ícone (§4/§5) */}
            <button
                type="button"
                onClick={() => onPreview?.(attachment)}
                className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800"
                aria-label={`Visualizar ${attachment.originalFileName}`}
                data-testid={`attachment-thumb-${attachment._id}`}
            >
                {isImage && url ? (
                    <img
                        src={url}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                        {attachment.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}
                    </span>
                )}
            </button>

            {/* Metadados */}
            <div className="min-w-0 flex-1">
                <button
                    type="button"
                    className="block w-full truncate text-left text-sm font-medium text-gray-900 hover:underline dark:text-gray-100"
                    onClick={() => onPreview?.(attachment)}
                    title={attachment.originalFileName}
                    data-testid={`attachment-name-${attachment._id}`}
                >
                    {attachment.originalFileName}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {attachmentCategoryLabel[attachment.category] ?? 'Documento'}
                    {' • '}
                    {fileSizeLabel(attachment.sizeBytes)}
                    {attachment.uploadedBy?.name
                        ? ` • ${attachment.uploadedBy.name}`
                        : ''}
                    {attachment.createdAt
                        ? ` • ${formatDatePt(attachment.createdAt)}`
                        : ''}
                </p>
            </div>

            {/* Ações */}
            <div className="flex shrink-0 items-center gap-1">
                {attachment.status === 'AVAILABLE' && (
                    <>
                        <button
                            type="button"
                            disabled={opening}
                            onClick={() => onPreview?.(attachment)}
                            className="rounded border border-gray-200 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800"
                            data-testid={`preview-attachment-${attachment._id}`}
                        >
                            Visualizar
                        </button>
                        <button
                            type="button"
                            onClick={() => onDownload?.(attachment)}
                            className="rounded border border-gray-200 px-2 py-0.5 text-xs hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                            data-testid={`download-attachment-${attachment._id}`}
                        >
                            Baixar
                        </button>
                    </>
                )}

                {onRemove && (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-label="Mais ações"
                            className="rounded px-1.5 py-0.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
                            data-testid={`attachment-menu-${attachment._id}`}
                        >
                            ⋯
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                                {confirmRemove ? (
                                    <div className="px-3 py-2 text-xs">
                                        <p className="mb-2 text-gray-700 dark:text-gray-200">
                                            Remover este documento?
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                className="text-od-secondary-600"
                                                onClick={() => {
                                                    onRemove(attachment)
                                                    setConfirmRemove(false)
                                                    setMenuOpen(false)
                                                }}
                                                data-testid={`confirm-remove-attachment-${attachment._id}`}
                                            >
                                                Remover
                                            </button>
                                            <button
                                                type="button"
                                                className="text-gray-500"
                                                onClick={() => setConfirmRemove(false)}
                                            >
                                                Voltar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                                            onClick={() => setConfirmRemove(true)}
                                            data-testid={`delete-attachment-${attachment._id}`}
                                        >
                                            Remover documento
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </li>
    )
}

// ---------------------------------------------------------------------------
// AttachmentsSection — grupos humanos, vazios ocultos, contagem (§46/§47/§48)
// ---------------------------------------------------------------------------

interface AttachmentsSectionProps {
    data: ProcessAttachmentsAggregate | null
    loading?: boolean
    urlsByAttachment: Record<string, string>
    openingId?: string | null
    onPreview: (attachment: PurchasingAttachment) => void
    onDownload: (attachment: PurchasingAttachment) => void
    onRemove: (attachment: PurchasingAttachment) => void
    onUploadFor: (target: {
        entityType: AttachmentEntityType
        entityId: string
        suggestedCategory?: AttachmentCategory
    }) => void
}

export const AttachmentsSection = ({
    data,
    loading,
    urlsByAttachment = {},
    openingId,
    onPreview,
    onDownload,
    onRemove,
    onUploadFor,
}: AttachmentsSectionProps) => {
    const { hasPermission } = usePermissions()
    const canCreate = hasPermission(roles.attachment.permissions.create.key)

    const total = data
        ? data.process.length +
          data.quotes.reduce((a, g) => a + g.attachments.length, 0) +
          data.purchase.length +
          data.payments.reduce((a, g) => a + g.attachments.length, 0)
        : 0

    if (loading) {
        return <p className="text-sm text-gray-400">Carregando documentos…</p>
    }

    if (!data || total === 0) {
        return (
            <div
                className="rounded-md border border-dashed border-gray-300 p-6 text-center dark:border-gray-600"
                data-testid="attachments-empty"
            >
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Nenhum documento anexado a este processo.
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Adicione referências, propostas, documentos da compra ou
                    comprovantes conforme o processo avançar.
                </p>
                <div className="mt-3">
                    <PermissionWrapper
                        permission={roles.attachment.permissions.create.key}
                    >
                        <Button
                            variant={ButtonVariant.secondary}
                            data-testid="attachments-upload-first"
                            onClick={() =>
                                onUploadFor({
                                    entityType: 'PURCHASE_PROCESS',
                                    entityId: '',
                                })
                            }
                        >
                            + Adicionar documento
                        </Button>
                    </PermissionWrapper>
                    {!canCreate && (
                        <p
                            className="mt-2 text-xs italic text-gray-500 dark:text-gray-400"
                            data-testid="attachments-readonly-hint"
                        >
                            Você possui acesso somente de leitura aos
                            documentos deste processo.
                        </p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4" data-testid="attachments-list">
            <p
                className="text-sm text-gray-500 dark:text-gray-400"
                data-testid="attachments-count"
            >
                {total} {total === 1 ? 'arquivo' : 'arquivos'}
            </p>

            <GroupBlock
                title={attachmentEntityLabel.PURCHASE_PROCESS}
                attachments={data.process}
                urlsByAttachment={urlsByAttachment}
                openingId={openingId}
                onPreview={onPreview}
                onDownload={onDownload}
                onRemove={onRemove}
                onUpload={
                    canCreate
                        ? () =>
                              onUploadFor({
                                  entityType: 'PURCHASE_PROCESS',
                                  entityId: '',
                              })
                        : undefined
                }
            />

            {data.quotes.map((group: QuoteAttachmentGroup) => (
                <GroupBlock
                    key={group.entityId}
                    title={`${attachmentEntityLabel.SUPPLIER_QUOTE} — ${group.label ?? '(fornecedor)'}`}
                    attachments={group.attachments}
                    urlsByAttachment={urlsByAttachment}
                    openingId={openingId}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onRemove={onRemove}
                    onUpload={
                        canCreate
                            ? () =>
                                  onUploadFor({
                                      entityType: 'SUPPLIER_QUOTE',
                                      entityId: group.entityId,
                                      suggestedCategory:
                                          AttachmentCategory.SUPPLIER_QUOTE,
                                  })
                            : undefined
                    }
                />
            ))}

            <GroupBlock
                title={attachmentEntityLabel.PURCHASE}
                attachments={data.purchase}
                urlsByAttachment={urlsByAttachment}
                openingId={openingId}
                onPreview={onPreview}
                onDownload={onDownload}
                onRemove={onRemove}
                onUpload={
                    canCreate
                        ? () =>
                              onUploadFor({
                                  entityType: 'PURCHASE',
                                  entityId: '',
                              })
                        : undefined
                }
            />

            {data.payments.map((group: PaymentAttachmentGroup) => (
                <GroupBlock
                    key={group.entityId}
                    title={`${attachmentEntityLabel.PAYMENT} — ${formatBRL(group.amountCents ?? 0)}`}
                    attachments={group.attachments}
                    urlsByAttachment={urlsByAttachment}
                    openingId={openingId}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onRemove={onRemove}
                    onUpload={
                        canCreate
                            ? () =>
                                  onUploadFor({
                                      entityType: 'PAYMENT',
                                      entityId: group.entityId,
                                  })
                            : undefined
                    }
                />
            ))}
        </div>
    )
}

const GroupBlock = ({
    title,
    attachments,
    urlsByAttachment,
    openingId,
    onPreview,
    onDownload,
    onRemove,
    onUpload,
}: {
    title: string
    attachments: PurchasingAttachment[]
    urlsByAttachment: Record<string, string>
    openingId?: string | null
    onPreview: (a: PurchasingAttachment) => void
    onDownload: (a: PurchasingAttachment) => void
    onRemove: (a: PurchasingAttachment) => void
    onUpload?: (() => void) | undefined
}) => (
    <div>
        <div className="mb-1 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {title}
            </h3>
            {onUpload && (
                <PermissionWrapper
                    permission={roles.attachment.permissions.create.key}
                >
                    <button
                        type="button"
                        onClick={onUpload}
                        className="text-xs text-od-primary-600 underline hover:text-od-primary-700 dark:text-od-primary-400"
                    >
                        + Anexar
                    </button>
                </PermissionWrapper>
            )}
        </div>
        {attachments.length === 0 ? null : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {attachments.map((a) => (
                    <AttachmentRow
                        key={a._id}
                        attachment={a}
                        url={urlsByAttachment[a._id]}
                        opening={openingId === a._id}
                        onPreview={onPreview}
                        onDownload={onDownload}
                        onRemove={onRemove}
                    />
                ))}
            </ul>
        )}
    </div>
)

// ---------------------------------------------------------------------------
// PreviewAttachmentModal (§6/§7/§8/§9/§13/§14)
// ---------------------------------------------------------------------------

interface PreviewAttachmentModalProps extends Omit<ModalProps, 'children'> {
    attachment: PurchasingAttachment | null
    url?: string | null
    loadingUrl?: boolean
    urlError?: boolean
    onRetry?: () => void
    onDownload?: () => void
    downloading?: boolean
}

export const PreviewAttachmentModal = ({
    isOpen,
    onClose,
    attachment,
    url,
    loadingUrl,
    urlError,
    onRetry,
    onDownload,
    downloading,
}: PreviewAttachmentModalProps) => {
    if (!attachment) return null
    const isImage = imageMimes.includes(attachment.mimeType)

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Large}>
            <h2
                className="mb-1 truncate text-lg font-bold text-gray-900 dark:text-gray-100"
                data-testid="preview-title"
            >
                {attachment.originalFileName}
            </h2>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {attachmentCategoryLabel[attachment.category] ?? 'Documento'}{' '}
                • {fileSizeLabel(attachment.sizeBytes)} • Adicionado em{' '}
                {formatDatePt(attachment.createdAt)}
            </p>

            <div className="min-h-[300px] max-h-[60vh] overflow-auto rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900">
                {loadingUrl ? (
                    <p
                        className="py-16 text-center text-sm text-gray-500"
                        data-testid="preview-loading"
                    >
                        Carregando documento…
                    </p>
                ) : urlError || !url ? (
                    <div className="py-16 text-center" data-testid="preview-error">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            {attachment.mimeType === 'application/pdf'
                                ? 'Não foi possível exibir este PDF diretamente.'
                                : 'Não foi possível carregar este documento.'}
                        </p>
                        <div className="mt-3 flex justify-center gap-2">
                            <Button
                                variant={ButtonVariant.secondary}
                                onClick={onRetry}
                                data-testid="preview-retry"
                            >
                                Tentar novamente
                            </Button>
                            <Button
                                variant={ButtonVariant.secondary}
                                onClick={onDownload}
                                disabled={downloading}
                                data-testid="preview-download"
                            >
                                Baixar
                            </Button>
                        </div>
                    </div>
                ) : isImage ? (
                    <img
                        src={url}
                        alt={attachment.originalFileName}
                        className="mx-auto max-h-[55vh] object-contain"
                        data-testid="preview-image"
                    />
                ) : (
                    <iframe
                        src={url}
                        title={attachment.originalFileName}
                        className="h-[55vh] w-full rounded-md"
                        data-testid="preview-pdf"
                    />
                )}
            </div>

            <ModalFooter>
                {attachment.mimeType === 'application/pdf' && url && (
                    <Button
                        variant={ButtonVariant.secondary}
                        onClick={() => window.open(url, '_blank', 'noopener')}
                        data-testid="preview-open-tab"
                    >
                        Abrir em nova aba
                    </Button>
                )}
                <Button variant={ButtonVariant.secondary} onClick={onClose}>
                    Fechar
                </Button>
                <Button
                    variant={ButtonVariant.primary}
                    onClick={onDownload}
                    disabled={downloading}
                    data-testid="preview-download-primary"
                >
                    {downloading ? 'Baixando…' : 'Baixar'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

// ---------------------------------------------------------------------------
// AttachmentUploadDialog — compacto, labels visíveis, preview local (§44/§45)
// ---------------------------------------------------------------------------

interface AttachmentUploadDialogProps extends Omit<ModalProps, 'children'> {
    entityType: AttachmentEntityType
    entityId: string
    suggestedCategory?: AttachmentCategory
    uploading?: boolean
    progress?: number
    error?: string | null
    onConfirm: (payload: {
        category: AttachmentCategory
        description?: string
        file: File
    }) => void
}

export const AttachmentUploadDialog = ({
    isOpen,
    onClose,
    entityType,
    suggestedCategory,
    uploading,
    progress,
    error,
    onConfirm,
}: AttachmentUploadDialogProps) => {
    const [file, setFile] = useState<File | null>(null)
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
    const [category, setCategory] = useState<AttachmentCategory>(
        suggestedCategory ?? ATTACHMENT_CATEGORIES_BY_ENTITY[entityType][0]
    )
    const [description, setDescription] = useState('')
    const [validationError, setValidationError] = useState<string | null>(null)

    const categories = ATTACHMENT_CATEGORIES_BY_ENTITY[entityType]

    useEffect(() => {
        return () => {
            if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl)
        }
    }, [localPreviewUrl])

    const reset = () => {
        if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl)
        setLocalPreviewUrl(null)
        setFile(null)
        setCategory(
            suggestedCategory ?? ATTACHMENT_CATEGORIES_BY_ENTITY[entityType][0]
        )
        setDescription('')
        setValidationError(null)
    }

    const handleFile = (f: File | null) => {
        setValidationError(null)
        if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl)
        setLocalPreviewUrl(null)
        if (!f) {
            setFile(null)
            return
        }
        if (!ALLOWED_MIME.includes(f.type)) {
            setValidationError(
                'Arquivo não suportado. Use PDF ou imagem (JPEG/PNG/WebP).'
            )
            setFile(null)
            return
        }
        if (f.size <= 0 || f.size > MAX_ATTACHMENT_SIZE_BYTES) {
            setValidationError('Arquivo excede o limite permitido (20 MB).')
            setFile(null)
            return
        }
        setFile(f)
        if (imageMimes.includes(f.type)) {
            setLocalPreviewUrl(URL.createObjectURL(f))
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h2 className="mb-2 text-lg font-bold">Adicionar documento</h2>

            {!uploading ? (
                <>
                    <label className="mb-2 block">
                        <span className="mb-0.5 block text-[11px] text-gray-500">
                            Arquivo
                        </span>
                        <span className="block cursor-pointer rounded-md border border-dashed border-gray-300 p-3 text-center text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
                            {file ? file.name : 'Selecionar arquivo'}
                        </span>
                        <input
                            type="file"
                            accept={ALLOWED_MIME.join(',')}
                            className="hidden"
                            onChange={(e) =>
                                handleFile(e.target.files?.[0] ?? null)
                            }
                            data-testid="attachment-file-input"
                        />
                    </label>

                    {file && (
                        <div className="mb-2 flex items-center gap-3 rounded-md border border-gray-200 p-2 dark:border-gray-600">
                            {localPreviewUrl ? (
                                <img
                                    src={localPreviewUrl}
                                    alt="Prévia"
                                    className="h-12 w-12 rounded object-cover"
                                    data-testid="local-preview"
                                />
                            ) : (
                                <span className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-[10px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                    PDF
                                </span>
                            )}
                            <div className="min-w-0">
                                <p className="truncate text-sm text-gray-900 dark:text-gray-100">
                                    {file.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {file.type.includes('image')
                                        ? 'Imagem'
                                        : 'PDF'}{' '}
                                    • {fileSizeLabel(file.size)}
                                </p>
                            </div>
                        </div>
                    )}

                    {validationError && (
                        <p
                            className="mb-2 text-sm text-od-secondary-600"
                            data-testid="attachment-validation-error"
                        >
                            {validationError}
                        </p>
                    )}

                    <label className="mb-2 block">
                        <span className="mb-0.5 block text-[11px] text-gray-500">
                            Categoria
                        </span>
                        <select
                            value={category}
                            onChange={(e) =>
                                setCategory(e.target.value as AttachmentCategory)
                            }
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            data-testid="attachment-category"
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {attachmentCategoryLabel[c]}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="block">
                        <span className="mb-0.5 block text-[11px] text-gray-500">
                            Descrição (opcional)
                        </span>
                        <input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            data-testid="attachment-description"
                        />
                    </label>

                    {error && (
                        <p
                            className="mt-2 text-sm text-od-secondary-600"
                            data-testid="attachment-upload-error"
                        >
                            {error}
                        </p>
                    )}

                    <ModalFooter>
                        <Button
                            variant={ButtonVariant.secondary}
                            onClick={onClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant={ButtonVariant.primary}
                            disabled={!file}
                            onClick={() =>
                                file &&
                                onConfirm({ category, description, file })
                            }
                            data-testid="confirm-attachment-upload"
                        >
                            Enviar documento
                        </Button>
                    </ModalFooter>
                </>
            ) : (
                <div
                    className="py-6 text-center"
                    data-testid="attachment-uploading"
                >
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Enviando arquivo… {progress ?? 0}%
                    </p>
                    <div className="mx-auto mt-2 h-2 w-full max-w-xs overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                            className="h-full bg-od-primary-500 transition-all"
                            style={{ width: `${progress ?? 0}%` }}
                        />
                    </div>
                </div>
            )}
        </Modal>
    )
}
